import { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Pagination } from "@/components/pagination"
import { AdminHeader } from "@/components/admin-header"
import { Search, Trash2, ExternalLink, Loader2 } from "lucide-react"
import type AdminSample from "@/types/generated/AdminSample"
import type { Pagination as PaginationType } from "@/types"

type Props = {
  samples: AdminSample[]
  pagination: PaginationType
  query: string | null
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function Index({ samples, pagination, query }: Props) {
  const [search, setSearch] = useState(query || "")
  const [deleteTarget, setDeleteTarget] = useState<AdminSample | null>(null)
  const [deleting, setDeleting] = useState(false)

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    router.get("/admin/samples", search ? { q: search } : {}, { preserveState: true })
  }

  const handleDelete = () => {
    if (!deleteTarget) return
    setDeleting(true)
    router.delete(`/admin/samples/${deleteTarget.id}`, {
      onSuccess: () => setDeleteTarget(null),
      onFinish: () => setDeleting(false),
    })
  }

  return (
    <div className="min-h-screen">
      <Head title="Admin - Samples" />
      <AdminHeader />

      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-mono font-bold">SAMPLES</h1>
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search samples..."
                className="pl-9 w-64 font-mono"
              />
            </div>
            <Button type="submit" variant="secondary">Search</Button>
          </form>
        </div>

        <Card>
          <div className="divide-y divide-border">
            {samples.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                No samples found
              </div>
            ) : (
              samples.map((sample) => (
                <div key={sample.id} className="p-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{sample.title}</span>
                      <Badge variant="secondary" className="text-xs">
                        {sample.analysis_status}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center gap-3 mt-1">
                      <span>by {sample.user.display_name || "Anonymous"} ({sample.user.name})</span>
                      <span className="font-mono">{formatFileSize(sample.file_size)}</span>
                      <span>{formatDate(sample.created_at)}</span>
                    </div>
                    {sample.tags.length > 0 && (
                      <div className="flex gap-1 mt-2">
                        {sample.tags.map((tag) => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" asChild>
                      <Link href={`/samples/${sample.id}`}>
                        <ExternalLink className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(sample)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        <Pagination pagination={pagination} baseUrl="/admin/samples" />
      </main>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !deleting && !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sample</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-destructive hover:bg-destructive/90">
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
